import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { CHAT_W, CHAT_H, CHAT_BLUE } from './constants';
import { API_BASE } from './lobbyReducer';
import { formatChatTime, getInitials, avatarBg } from './helpers';

// ── Single floating chat window ───────────────────────────────────────────────
export function FloatingChatWindow({ chatWindow, isMinimized, onClose, onMinimize, stackIndex }) {
  const { channelId, title, authorName, mode, participantId } = chatWindow;

  const [messages,   setMessages]   = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [composing,  setComposing]  = useState('');
  const [sending,    setSending]    = useState(false);
  const bottomRef   = useRef(null);
  const messagesRef = useRef(null);
  const inputRef    = useRef(null);
  const pollRef     = useRef(null);

  const loadMessages = useCallback(async () => {
    if (!channelId) return;
    try {
      const params = mode === 'direct' && participantId
        ? `?limit=100&participantId=${encodeURIComponent(participantId)}`
        : '?limit=100';
      const res  = await fetch(`${API_BASE}/chat-channels/${channelId}/messages${params}`);
      const json = await res.json();
      if (json.success) setMessages(json.data || []);
    } catch {}
  }, [channelId, mode, participantId]);

  useEffect(() => {
    if (!channelId || isMinimized) return;
    setLoadingMsgs(true);
    loadMessages().finally(() => setLoadingMsgs(false));
    pollRef.current = setInterval(loadMessages, 5000);
    return () => clearInterval(pollRef.current);
  }, [channelId, isMinimized, loadMessages]);

  useEffect(() => {
    if (isMinimized) clearInterval(pollRef.current);
  }, [isMinimized]);

  // Scroll to bottom when new messages arrive (if not minimized)
  useEffect(() => {
    if (!isMinimized) {
      setTimeout(() => {
        if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
      }, 50);
    }
  }, [messages.length, isMinimized]);

  useEffect(() => {
    if (!isMinimized) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isMinimized]);

  const handleSend = useCallback(async () => {
    const text = composing.trim();
    if (!text || !channelId || sending) return;
    setComposing('');
    setSending(true);
    try {
      const body = {
        message: text, authorName: authorName || 'Preview Admin',
        authorType: 'participant',
        ...(mode === 'direct' && participantId ? { participantId, participantName: authorName } : {}),
      };
      const res  = await fetch(`${API_BASE}/chat-channels/${channelId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (json.success) setMessages(prev => [...prev, json.data]);
    } catch {}
    finally { setSending(false); }
  }, [composing, channelId, authorName, mode, participantId, sending]);

  const rightOffset = 16 + stackIndex * (CHAT_W + 12);

  return (
    <Box sx={{
      position: 'absolute', bottom: 0, right: rightOffset,
      width: CHAT_W, zIndex: 300 + stackIndex,
      display: 'flex', flexDirection: 'column',
      borderRadius: '12px 12px 0 0', overflow: 'hidden',
      boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
      height: isMinimized ? 48 : CHAT_H,
      transition: 'height 0.2s ease',
    }}>
      {/* ── Header bar ── */}
      <Box onClick={onMinimize} sx={{ height: 48, flexShrink: 0, background: CHAT_BLUE, display: 'flex', alignItems: 'center', gap: 1, px: 1.5, cursor: 'pointer', userSelect: 'none' }}>
        <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Box sx={{ fontSize: 14 }}>💬</Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title || 'Live Chat'}</Box>
          <Box sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.72)' }}>{channelId ? 'Live' : 'Not saved yet'}</Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <Box onClick={onMinimize} sx={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', cursor: 'pointer', color: 'rgba(255,255,255,0.85)', fontSize: 16, '&:hover': { background: 'rgba(255,255,255,0.15)' } }}>—</Box>
          <Box onClick={onClose}    sx={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', cursor: 'pointer', color: 'rgba(255,255,255,0.85)', fontSize: 14, '&:hover': { background: 'rgba(255,255,255,0.15)' } }}>✕</Box>
        </Box>
      </Box>

      {/* ── Body ── */}
      {!isMinimized && (
        <>
          {/* Messages */}
          <Box ref={messagesRef} sx={{ flex: 1, overflowY: 'auto', background: '#fff', px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: '2px', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { background: '#d4dbe6', borderRadius: 2 } }}>
            {loadingMsgs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress size={20} sx={{ color: CHAT_BLUE }} /></Box>
            ) : !channelId ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1, opacity: 0.5 }}>
                <Box sx={{ fontSize: 28 }}>💬</Box>
                <Box sx={{ fontSize: '0.75rem', color: '#9ea8b3', textAlign: 'center' }}>Save the lobby to activate this chat.</Box>
              </Box>
            ) : messages.filter(m => !m.isDeleted).length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1, opacity: 0.5 }}>
                <Box sx={{ fontSize: 28 }}>💬</Box>
                <Box sx={{ fontSize: '0.75rem', color: '#9ea8b3' }}>No messages yet</Box>
              </Box>
            ) : messages.filter(m => !m.isDeleted).map((msg, i, arr) => {
              const isOwn     = msg.authorName === authorName;
              const prevMsg   = arr[i - 1];
              const showAvatar = !isOwn && (i === 0 || prevMsg?.authorName !== msg.authorName);
              return (
                <Box key={msg._id} sx={{ display: 'flex', flexDirection: isOwn ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 0.75, mt: showAvatar ? 1 : 0 }}>
                  {!isOwn && (
                    <Box sx={{ width: 28, flexShrink: 0 }}>
                      {showAvatar && (
                        <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: avatarBg(msg.authorName), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.62rem', fontWeight: 700, color: '#fff' }}>
                          {getInitials(msg.authorName)}
                        </Box>
                      )}
                    </Box>
                  )}
                  <Box sx={{ maxWidth: '75%', display: 'flex', flexDirection: 'column', alignItems: isOwn ? 'flex-end' : 'flex-start' }}>
                    {showAvatar && !isOwn && <Box sx={{ fontSize: '0.65rem', color: '#9ea8b3', mb: 0.3, px: 0.5 }}>{msg.authorName}</Box>}
                    <Box sx={{ px: 1.5, py: 0.9, borderRadius: isOwn ? '18px 18px 4px 18px' : '18px 18px 18px 4px', background: isOwn ? CHAT_BLUE : '#f0f4f8', color: isOwn ? '#fff' : '#1a1a2e', fontSize: '0.82rem', lineHeight: 1.45, wordBreak: 'break-word' }}>
                      {msg.message}
                    </Box>
                    <Box sx={{ fontSize: '0.6rem', color: '#c4cdd8', mt: 0.3, px: 0.5 }}>{formatChatTime(msg.createdAt)}</Box>
                  </Box>
                </Box>
              );
            })}
            <div ref={bottomRef} style={{ display: 'none' }} />
          </Box>

          {/* Compose */}
          {channelId && (
            <Box sx={{ flexShrink: 0, background: '#fff', borderTop: '1px solid #f0f4f8', px: 1.25, py: 1, display: 'flex', alignItems: 'flex-end', gap: 0.75 }}>
              <Box sx={{ flex: 1, background: '#f5f7fa', borderRadius: '20px', px: 1.5, py: 0.75 }}>
                <textarea
                  ref={inputRef}
                  value={composing}
                  onChange={e => setComposing(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  placeholder="Aa" rows={1}
                  style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', resize: 'none', fontSize: '0.82rem', fontFamily: 'inherit', color: '#1a1a2e', lineHeight: 1.4, maxHeight: 80, overflowY: 'auto' }}
                />
              </Box>
              <Box onClick={handleSend} sx={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, background: composing.trim() ? CHAT_BLUE : '#e8edf2', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: composing.trim() ? 'pointer' : 'default', transition: 'background 0.15s' }}>
                <Box sx={{ fontSize: 14, color: composing.trim() ? '#fff' : '#b0bac8', transform: 'rotate(-45deg)', mt: '-2px' }}>➤</Box>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

// ── Manager — renders all open chat windows ───────────────────────────────────
export function FloatingChatManager({ openChats, minimized, onClose, onMinimize, authorName, participantId }) {
  if (!openChats.length) return null;
  return (
    <>
      {openChats.map((cw, i) => (
        <FloatingChatWindow
          key={cw.id}
          chatWindow={{ ...cw, authorName, participantId }}
          stackIndex={i}
          isMinimized={!!minimized[cw.id]}
          onClose={() => onClose(cw.id)}
          onMinimize={() => onMinimize(cw.id)}
        />
      ))}
    </>
  );
}